import {createSlice} from '@reduxjs/toolkit';
import {Location, UserLocation} from '../../api/types';
import {ForecastModelConfig} from '../../utils/Forecast/types';

type LocationState = {
  locationList: Location[];
  userLocation: UserLocation | null;
  selectedLocation: string | null;

  favoriteLocationHashs: string[];

  forecastModels: Record<string, ForecastModelConfig[]>;
  selectedForecastModel: Record<string, string>;
};

export const initialState: LocationState = {
  locationList: [],
  userLocation: null,
  selectedLocation: null,
  favoriteLocationHashs: [],
  forecastModels: {},
  selectedForecastModel: {},
};

export const weatherSlice = createSlice({
  name: 'location',
  initialState,
  reducers: {
    addLocation: (
      state,
      {payload: newLocation}: {payload: Location | null},
    ) => {
      state.locationList = state.locationList
        .filter((l) => l.hash !== newLocation?.hash)
        .concat([newLocation]);
    },
    addFavoriteLocation: (
      state,
      {payload: locationHash}: {payload: string},
    ) => {
      state.favoriteLocationHashs.push(locationHash);
    },
    removeFavoriteLocation: (
      state,
      {payload: locationHash}: {payload: string},
    ) => {
      state.favoriteLocationHashs = state.favoriteLocationHashs.filter(
        (hash) => hash !== locationHash,
      );
      state.locationList = state.locationList.filter(
        (l) => l.hash !== locationHash,
      );
    },
    reorderFavoriteLocations: (
      state,
      {payload: orderedHashes}: {payload: string[]},
    ) => {
      state.favoriteLocationHashs = orderedHashes;
    },
    cleanLocationList: (state) => {
      // Keep only favorite locations
      state.locationList = state.locationList.filter((l) =>
        state.favoriteLocationHashs.includes(l.hash),
      );
    },
    setUserLocation: (
      state,
      {payload: userLocation}: {payload: UserLocation | null},
    ) => {
      state.userLocation = userLocation;
    },

    setSelectedLocation: (state, {payload: hash}: {payload: string}) => {
      state.selectedLocation = hash;
    },

    // Weather Forecast reducers
    setLocationForecastModels: (
      state,
      {
        payload: {hash, forecastModels},
      }: {
        payload: {
          hash: string;
          forecastModels: ForecastModelConfig[];
        };
      },
    ) => {
      const newForecastModels = {} as Record<number, ForecastModelConfig[]>;
      newForecastModels[hash] = forecastModels;
      state.forecastModels = {...state.forecastModels, ...newForecastModels};
    },
    setLocationSelectedForecastModel: (
      state,
      {
        payload: {locationHash, forecastModel},
      }: {payload: {locationHash: string; forecastModel: string}},
    ) => {
      const newSelectedForecastModel = {} as Record<string, any>;
      newSelectedForecastModel[locationHash] = forecastModel;
      state.selectedForecastModel = {
        ...state.selectedForecastModel,
        ...newSelectedForecastModel,
      };
    },
  },
});

export const {
  setUserLocation,
  setSelectedLocation,
  setLocationForecastModels,
  setLocationSelectedForecastModel,
  addLocation,
  addFavoriteLocation,
  removeFavoriteLocation,
  reorderFavoriteLocations,
  cleanLocationList,
} = weatherSlice.actions;

export default weatherSlice.reducer;
